import * as _ from "lodash";

export function isMobileOrderCartCustomOptionVisible(option: any, user: any) {
  if (!option || !option.active) {
    return false;
  }

  if (option.preNameId === "partial_payment") {
    return option.upos_active === true && user?.uposMoUser === true;
  }

  return !option.only_kiosk;
}

export function sanitizeCartCustomOptionNumber(value: any) {
  if (_.isNil(value) || value === "") {
    return null;
  }

  const parsedValue = parseFloat(value);

  if (_.isNaN(parsedValue)) {
    return null;
  }

  return parsedValue;
}

export function isCartCustomOptionChoiceSelected(value: any) {
  return (
    value === true ||
    value === "true" ||
    value === 1 ||
    value === "1"
  );
}

function roundCartCustomOptionPrice(value: number) {
  return parseFloat(value.toFixed(2));
}

export function normalizeCartCustomOptionSelection(option: any) {
  const normalizedOption: any = _.cloneDeep(option);

  if (
    !normalizedOption ||
    !normalizedOption.choices ||
    !_.isArray(normalizedOption.choices)
  ) {
    return normalizedOption;
  }

  normalizedOption.choices = _.map(normalizedOption.choices, (choice) =>
    normalizeCartCustomChoiceSelection(normalizedOption, choice)
  );

  return normalizedOption;
}

function normalizeCartCustomChoiceSelection(option: any, choice: any) {
  const normalizedChoice: any = _.cloneDeep(choice);

  if (!normalizedChoice) {
    return normalizedChoice;
  }

  const price = sanitizeCartCustomOptionNumber(normalizedChoice.price);
  const qty = sanitizeCartCustomOptionNumber(normalizedChoice.qty);
  const selected = isCartCustomOptionChoiceSelected(normalizedChoice.selected);
  const hasText = !!normalizedChoice.text;
  const quantityOption = !!(option && option.qtyEnabled);
  const qtyDrivenSelection = quantityOption && qty > 0;
  const shouldSelect = selected || qtyDrivenSelection || hasText;

  normalizedChoice.selected = shouldSelect;

  if (!_.isNil(price)) {
    normalizedChoice.price = price;
  }

  if (!_.isNil(price) && price > 0 && shouldSelect) {
    const normalizedQty = qty && qty > 0 ? qty : 1;
    normalizedChoice.qty = normalizedQty;
    normalizedChoice.totalPrice = roundCartCustomOptionPrice(
      price * normalizedQty
    );
    return normalizedChoice;
  }

  if (quantityOption) {
    normalizedChoice.qty = qty && qty > 0 ? qty : 0;
  } else {
    delete normalizedChoice.qty;
  }

  delete normalizedChoice.totalPrice;

  return normalizedChoice;
}

export function buildCartCustomOptionPayload(option: any) {
  const normalizedOption = normalizeCartCustomOptionSelection(option);

  if (
    !normalizedOption ||
    !normalizedOption.choices ||
    !_.isArray(normalizedOption.choices)
  ) {
    return null;
  }

  const choices = _.compact(
    _.map(normalizedOption.choices, (choice) =>
      buildCartCustomChoicePayload(normalizedOption, choice)
    )
  );

  if (_.isEmpty(choices)) {
    return null;
  }

  const params: any = {
    title: normalizedOption.title,
    preNameId: normalizedOption.preNameId,
    onlinePaymentOnly: normalizedOption.onlinePaymentOnly,
    timestamp: normalizedOption.timestamp,
    type: normalizedOption.type,
    isRequired: normalizedOption.isRequired,
    do_not_print_title: normalizedOption.do_not_print_title,
    minOrderCheckChoices: normalizedOption.minOrderCheckChoices,
    position: normalizedOption.position,
    disabledOnPreorder: normalizedOption.disabledOnPreorder,
    vat_classification_category:
      normalizedOption.vat_classification_category || null,
    choices,
  };

  // Marks the typed dine-in table number option. dm-mo-create-order copies the
  // typed value into order.bridge_dinein_table_id/name for the POS bridge, so
  // the flag has to survive this whitelist.
  if (normalizedOption.bridgeDineinTableId) {
    params.bridgeDineinTableId = true;
  }

  return params;
}

function buildCartCustomChoicePayload(option: any, choice: any) {
  if (!choice) {
    return null;
  }

  const selected = isCartCustomOptionChoiceSelected(choice.selected);
  const qty = sanitizeCartCustomOptionNumber(choice.qty);
  const price = sanitizeCartCustomOptionNumber(choice.price);
  const totalPrice = sanitizeCartCustomOptionNumber(choice.totalPrice);
  const hasText = !!choice.text;

  if (!selected && !(option && option.qtyEnabled && qty > 0) && !hasText) {
    return null;
  }

  const params: any = {
    title: choice.title,
    timestamp: choice.timestamp,
  };

  if (!_.isNil(price)) {
    params.price = price;
  }

  if (!_.isNil(qty) && qty > 0) {
    params.qty = qty;
  }

  if (!_.isNil(totalPrice) && totalPrice !== 0) {
    params.totalPrice = totalPrice;
  }

  if (selected) {
    params.selected = true;
  }

  if (choice.text) {
    params.text = choice.text;
  }

  if (!_.isNil(choice.minOrder) && choice.minOrder !== "") {
    params.minOrder = choice.minOrder;
  }

  return params;
}

// The dine-in table number describes the table the customer is sitting at right
// now. It is persisted along with the other cart custom options so that a
// payment method change does not lose what was just typed, but a restarted app
// must forget it: a remembered value would satisfy the required check, the popup
// would stay closed, and the previous table number would reach the POS.
// Dropping the whole option leaves the store config default (blank) in place.
export function stripBridgeDineinTableFromStoredCartCustomOptions(
  storedCartCustomOptions: any
) {
  if (!_.isArray(storedCartCustomOptions)) {
    return storedCartCustomOptions;
  }

  return _.filter(
    storedCartCustomOptions,
    (option) => !(option && option.bridgeDineinTableId)
  );
}

export function mergeStoredCartCustomOptionsIntoSelections(
  storeInfo: any,
  storedCartCustomOptions: any
) {
  const selectedCustomOptions = _.cloneDeep(
    storeInfo && storeInfo.cartCustomOptions ? storeInfo.cartCustomOptions : []
  );

  if (!_.isArray(storedCartCustomOptions)) {
    return _.map(selectedCustomOptions, (option) =>
      normalizeCartCustomOptionSelection(option)
    );
  }

  _.each(storedCartCustomOptions, (storedOption) => {
    if (!storedOption || !_.isArray(storedOption.choices)) {
      return;
    }

    const customOptionIndex = _.findIndex(selectedCustomOptions, {
      timestamp: storedOption.timestamp,
    });

    if (
      customOptionIndex === -1 ||
      !selectedCustomOptions[customOptionIndex].active ||
      !_.isArray(selectedCustomOptions[customOptionIndex].choices)
    ) {
      return;
    }


    _.each(selectedCustomOptions[customOptionIndex].choices, (choice) => {
      const storedChoice = _.find(storedOption.choices || [], {
        timestamp: choice.timestamp,
      });

      if (!storedChoice) {
        return;
      }

      if (
        selectedCustomOptions[customOptionIndex].type === "text" &&
        storedChoice.text
      ) {
        choice.text = storedChoice.text;
      }

      choice.selected = isCartCustomOptionChoiceSelected(storedChoice.selected);

      if (selectedCustomOptions[customOptionIndex].qtyEnabled) {
        const storedQty = sanitizeCartCustomOptionNumber(storedChoice.qty);
        choice.qty = storedQty && storedQty > 0 ? storedQty : 0;
      }
    });
  });

  return _.map(selectedCustomOptions, (option) =>
    normalizeCartCustomOptionSelection(option)
  );
}
